import { DetailsTabs, PanelTypes, PanelType, DetailsTab } from '../constants';
import { ValueOf } from '../types/common';
import { InsertionCriteria } from '../types/insertionCriteria';

import { FrameStateTypes, frameStateBuilder } from './frameState';

export { DetailsTabs, PanelTypes };

export const DetailPanelActionButtons = {
  SHARE: 'SHARE',
  EDIT: 'EDIT',
  INSERT: 'INSERT',
  ARCHIVE: 'ARCHIVE',
  DOWNLOAD: 'DOWNLOAD',
  REMIX: 'REMIX',
  EDIT_IN_DESCRIPT: 'EDIT_IN_DESCRIPT',
  COPY_URL: 'COPY_URL',
  OPEN_IN_NEW_TAB: 'OPEN_IN_NEW_TAB',
  MOVE_TO_TRASH: 'MOVE_TO_TRASH',
  DIVIDER: 'DIVIDER',
  DONE: 'DONE',
} as const;

export type PanelActionButtonConfig<T extends String> = {
  type: T;
};

export type DetailPanelActionButtonConfig = PanelActionButtonConfig<
  ValueOf<typeof DetailPanelActionButtons>
>;

export type ManagementPanelConfig = {
  panelActions: {
    primary: DetailPanelActionButtonConfig;
    secondary?: DetailPanelActionButtonConfig;
    tertiary?: DetailPanelActionButtonConfig[];
  };
};

export type VideoPanelConfig = {
  [PanelTypes.DETAILS]: ManagementPanelConfig;
};

export type VideoPanelState = {
  activePanel: null | PanelType;
  enableAdvancedFeaturesOnInsert: boolean;
  enablePrivateInsert: boolean;
  insertionCriteria: InsertionCriteria[];
  hostApp?: string;
  isManagementPanelLoading: boolean;
  state: {
    [PanelTypes.DETAILS]: {
      fileId?: number;
      selectedTab: DetailsTab;
    };
    [PanelTypes.PICKER]: {
      allowUpload: boolean;
      allowBulkSelect: boolean;
      bulkSelectionLimit: number;
    };
    [PanelTypes.CLIP_EDITOR]: {
      projectId: null | number;
      referrerPanel: null | PanelType;
      isLoading: boolean;
    };
  };
  config: VideoPanelConfig;
};

export const COMMON_ACTIONS: DetailPanelActionButtonConfig[] = [
  { type: 'DOWNLOAD' },
  { type: 'REMIX' },
  { type: 'EDIT_IN_DESCRIPT' },
  { type: 'DIVIDER' },
  { type: 'COPY_URL' },
  { type: 'OPEN_IN_NEW_TAB' },
  { type: 'DIVIDER' },
  { type: 'ARCHIVE' },
];

export const getDetailsConfig = ({
  showDelete = false,
  showShare = true,
}: { showDelete?: boolean; showShare?: boolean } = {}) => {
  return {
    panelActions: {
      primary: { type: showShare ? 'SHARE' : 'DONE' },
      secondary: { type: 'EDIT' },
      tertiary: [
        ...COMMON_ACTIONS,
        ...(showDelete ? [{ type: 'MOVE_TO_TRASH' }] : []),
      ],
    },
  } as ManagementPanelConfig;
};

const getCurrentApp = () => {
  // @ts-expect-error hubspot.bender lacks types
  if (!(window.hubspot && window.hubspot.bender)) {
    return undefined;
  }

  return (window.hubspot as any).bender.currentProject;
};

// NOTE: If you are adding a new value to state and referencing it in video-management-ui's
// EmbedVideoPanelState, CHECK FOR EXPLICIT VALUES (e.g. not truthy/falsy). The value may
// be undefined in consuming apps that have not deployed with the latest version of this libary.
export const videoPanelStateBuilder = frameStateBuilder()
  .setInitialState({
    activePanel: null,
    enableAdvancedFeaturesOnInsert: true,
    enablePrivateInsert: false,
    insertionCriteria: [],
    hostApp: getCurrentApp(),
    isManagementPanelLoading: false,
    state: {
      [PanelTypes.DETAILS]: {
        selectedTab: DetailsTabs.OPTIMIZATION,
      },
      [PanelTypes.PICKER]: {
        allowUpload: true,
        allowBulkSelect: false,
        bulkSelectionLimit: 10,
      },
      [PanelTypes.CLIP_EDITOR]: {
        projectId: null,
        referrerPanel: null,
        isLoading: false,
      },
    },
    config: {
      [PanelTypes.DETAILS]: getDetailsConfig(),
    },
  } as VideoPanelState)
  .setStateName('@@video-components-external/VideoPanelState');

export const VideoPanelState = videoPanelStateBuilder
  .setStateType(FrameStateTypes.LEADER)
  .build();

export const isStandaloneDetailsPanel = (panelState: VideoPanelState) => {
  const primaryPanelAction =
    panelState.config[PanelTypes.DETAILS].panelActions.primary.type;
  return (
    panelState.activePanel === PanelTypes.DETAILS &&
    (primaryPanelAction === 'SHARE' || primaryPanelAction === 'DONE')
  );
};
